import { useState, type KeyboardEvent } from 'react';
import { Send, StopCircle } from 'lucide-react';
import { cn } from '../../lib/utils';

interface ChatInputProps {
  onSend: (message: string) => void;
  isLoading?: boolean;
  onStop?: () => void;
}

export function ChatInput({ onSend, isLoading, onStop }: ChatInputProps) {
  const [input, setInput] = useState('');

  const handleSend = () => {
    if (input.trim() && !isLoading) {
      onSend(input.trim());
      setInput('');
    }
  };

  const handleStop = () => {
    if (onStop) {
      onStop();
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isLoading) {
        handleSend();
      }
    }
  };

  return (
    <div className="bg-transparent p-3 sm:p-4">
      <div className="mx-auto max-w-3xl">
        <div className="relative flex items-center gap-2 sm:gap-3 border-b border-gray-300 bg-transparent px-1 sm:px-2 py-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Reply..."
            disabled={isLoading}
            rows={1}
            className={cn(
              'flex-1 resize-none bg-transparent px-0 py-2',
              'text-[1.375rem] sm:text-lg text-gray-900 placeholder:text-gray-500 font-family-messina-sans',
              'focus:outline-none border-0 focus:ring-0 focus:border-0',
              'disabled:cursor-not-allowed disabled:opacity-50',
              'min-h-[40px] max-h-[200px]'
            )}
            style={{ height: 'auto', border: 'none', outline: 'none', boxShadow: 'none' }}
            onInput={(e) => {
              const target = e.target as HTMLTextAreaElement;
              target.style.height = 'auto';
              target.style.height = `${Math.min(target.scrollHeight, 200)}px`;
            }}
          />
          <div className="self-stretch w-px bg-border my-1"></div>
          <button
            onClick={isLoading ? handleStop : handleSend}
            disabled={!isLoading && !input.trim()}
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-lg border border-border',
              isLoading
                ? 'bg-destructive/10 text-destructive hover:bg-destructive hover:text-destructive-foreground'
                : 'bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground',
              'hover:shadow-md hover:scale-105 transition-all cursor-pointer',
              'disabled:cursor-not-allowed disabled:opacity-30'
            )}
          >
            {isLoading ? (
              <StopCircle className="h-4 w-4" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </button>
        </div>
        <p className="mt-2 text-center text-xs text-muted-foreground italic">
          Nebula is AI and can make mistakes. Please double-check responses.
        </p>
      </div>
    </div>
  );
}
